import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  FileText, Package, GitBranch, Box, Lock, CheckCircle, 
  ArrowRight, LogOut, Building2, Key
} from "lucide-react";
import { BrandedFooter } from "@/components/BrandedFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_docuflow-68/artifacts/y7jajers_Well%20Connected%20Logo_Feb2024_600px.jpg";

// Icon mapping
const iconMap = {
  FileText: FileText,
  Package: Package,
  GitBranch: GitBranch,
  Box: Box,
};

export default function AppCatalog() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [userAppAccess, setUserAppAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [licenseKey, setLicenseKey] = useState("");
  const [activating, setActivating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchApps();
    fetchUserAccess();
  }, []);

  const fetchApps = async () => {
    try {
      const response = await axios.get(`${API}/applications`);
      setApps(response.data);
    } catch (error) {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAccess = async () => {
    try {
      const response = await axios.get(`${API}/user/app-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserAppAccess(response.data);
    } catch (error) {
      console.error("Failed to fetch user access:", error);
    }
  };

  const hasAccess = (appId) => {
    return userAppAccess.some(a => a.app_id === appId);
  };

  const getAccessInfo = (appId) => {
    return userAppAccess.find(a => a.app_id === appId);
  };

  const handleAppClick = (app) => {
    if (app.status !== "active") {
      toast.info(`${app.name} is coming soon!`);
      return;
    }

    const access = getAccessInfo(app.app_id);
    if (access) {
      // User has access, navigate to app
      navigate(app.route_path);
    } else {
      // Need to activate license
      setSelectedApp(app);
      setLicenseKey("");
      setDialogOpen(true);
    }
  };

  const handleActivateLicense = async () => {
    if (!licenseKey.trim()) {
      toast.error("Please enter a license key");
      return;
    }

    setActivating(true);
    try {
      // Use the new user activation endpoint that persists to account
      const response = await axios.post(`${API}/user/activate-license`, {
        license_key: licenseKey.trim(),
        app_id: selectedApp.app_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(`${selectedApp.name} activated successfully!`);
        setDialogOpen(false);
        fetchUserAccess();
        // Navigate to the app
        setTimeout(() => navigate(selectedApp.route_path), 500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate license");
    } finally {
      setActivating(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Call backend to clear server-side session and cookies
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.log("Logout API error (non-critical):", error);
    }
    // Clear local storage
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const IconComponent = ({ iconName, className }) => {
    const Icon = iconMap[iconName] || Box;
    return <Icon className={className} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E01626]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="WELL Connected" className="h-12" />
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-medium text-sm">{user.name || user.email}</p>
              <p className="text-xs text-muted-foreground">Client Portal</p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-1" />
              <span className="text-sm">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-[#E01626] mb-2">
            Welcome, {user.name?.split(" ")[0] || "User"}
          </h1>
          <p className="text-lg text-muted-foreground">
            Select an application to get started. Contact your administrator for license keys.
          </p>
        </div>

        {/* Quick Stats */}
        {userAppAccess.length > 0 && (
          <div className="mb-8 p-4 bg-white rounded-lg border flex items-center gap-4">
            <div className="p-2 bg-green-100 rounded-full">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium">You have access to {userAppAccess.length} application(s)</p>
              <p className="text-sm text-muted-foreground">
                {userAppAccess.map(a => a.app_name).join(", ")}
              </p>
            </div>
          </div>
        )}

        {/* Applications Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((app) => {
            const access = getAccessInfo(app.app_id);
            const isActive = app.status === "active";
            const isComingSoon = app.status === "coming_soon";
            
            return (
              <Card 
                key={app.app_id}
                className={`relative overflow-hidden transition-all duration-300 cursor-pointer group
                  ${access ? "border-green-200 bg-green-50/30" : ""}
                  ${isComingSoon ? "opacity-70" : "hover:shadow-lg hover:border-slate-300"}
                `}
                onClick={() => handleAppClick(app)}
              >
                {/* Status Badge */}
                <div className="absolute top-4 right-4">
                  {access ? (
                    <Badge className="bg-green-500">
                      <CheckCircle className="h-3 w-3 mr-1" /> Active
                    </Badge>
                  ) : isComingSoon ? (
                    <Badge variant="secondary">Coming Soon</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-white">
                      <Lock className="h-3 w-3 mr-1" /> Locked
                    </Badge>
                  )}
                </div>

                <CardHeader className="pb-2">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: app.color + "15" }}
                  >
                    <IconComponent 
                      iconName={app.icon} 
                      className="h-7 w-7"
                      style={{ color: app.color }}
                    />
                  </div>
                  <CardTitle className="text-xl">{app.name}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {app.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  {/* Features */}
                  <div className="space-y-1 mb-4">
                    {app.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="w-1 h-1 rounded-full bg-slate-400" />
                        {feature}
                      </div>
                    ))}
                    {app.features.length > 3 && (
                      <p className="text-xs text-muted-foreground">+{app.features.length - 3} more features</p>
                    )}
                  </div>

                  {/* Action Button */}
                  <Button 
                    className="w-full group-hover:bg-opacity-90"
                    style={{ 
                      backgroundColor: access ? "#059669" : isActive ? app.color : "#94a3b8",
                    }}
                    disabled={isComingSoon}
                  >
                    {access ? (
                      <>Open App <ArrowRight className="h-4 w-4 ml-2" /></>
                    ) : isComingSoon ? (
                      "Coming Soon"
                    ) : (
                      <>Activate License <Key className="h-4 w-4 ml-2" /></>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Help Section */}
        <div className="mt-12 p-6 bg-white rounded-lg border text-center">
          <Building2 className="h-10 w-10 text-[#E01626] mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-2">Need access to an application?</h3>
          <p className="text-muted-foreground mb-4">
            Contact your administrator to get a license key for the applications you need.
          </p>
        </div>
      </main>

      <BrandedFooter />

      {/* License Activation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-[#1a3a6e]" />
              Activate {selectedApp?.name}
            </DialogTitle>
            <DialogDescription>
              Enter your license key to access this application.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              {selectedApp && (
                <>
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: selectedApp.color + "15" }}
                  >
                    <IconComponent 
                      iconName={selectedApp.icon} 
                      className="h-5 w-5"
                      style={{ color: selectedApp.color }}
                    />
                  </div>
                  <div>
                    <p className="font-medium">{selectedApp.name}</p>
                    <p className="text-xs text-muted-foreground">{selectedApp.description.slice(0, 50)}...</p>
                  </div>
                </>
              )}
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">License Key</label>
              <Input
                placeholder="XXXX-XXXX-XXXX-XXXX"
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
                className="font-mono text-center tracking-widest"
              />
            </div>

            <Button 
              className="w-full"
              style={{ backgroundColor: selectedApp?.color || "#1a3a6e" }}
              onClick={handleActivateLicense}
              disabled={activating || !licenseKey.trim()}
            >
              {activating ? "Activating..." : "Activate License"}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Don't have a license key? Contact your administrator.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
